import { Box, Typography } from "@mui/material";
import React from "react";
import AppButton from "../../base/AppButton.component";
import { useVerification } from "../../../context/Verification.context";
import Theme from "../../../utils/Theme";
import { useState } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_ARTIFACT_LOG_LANGUAGE } from "../../../graphql/mutation/mutation";

// Short-height landscape phones — same breakpoint used across the artifact
// screen (WarrantyStartPopup, etc.) so everything shrinks together instead
// of the toggle row staying full-size while content below it compresses.
const LANDSCAPE_SHORT = "@media (orientation: landscape) and (max-height: 500px)";

const ToggleComponent = ({
  handleClose,
  isAI,
  walkthrough,
  isClose = true,
}) => {
  const verification = useVerification();
  const [languageOpen, setLanguageOpen] = useState(false);
  const [modeOpen, setModeOpen] = useState(false);

  const [updateArtifactLogLanguage] = useMutation(UPDATE_ARTIFACT_LOG_LANGUAGE, {
    onCompleted: (data) => {
      console.log("Language updated in artifact log", data);
    },
    onError: (err) => {
      console.error("Error updating artifact log language", err);
    },
  });

  const handleLanguageChange = (isSpanish) => {
    verification.setAIData((prev) => ({
      ...prev,
      spanish: isSpanish,
      isLoading: true,
    }));
    setLanguageOpen(false);

    updateArtifactLogLanguage({
      variables: {
        updateArtifactLogLanguageInput: {
          dealership_id: Number(verification?.dealershipId),
          employee_user_id: Number(verification?.employeeId),
          user_id: Number(verification?.customer?.userId),
          language_id: isSpanish ? 2 : 1,
          updated_by: Number(verification?.employeeId),
        },
      },
    });
  };

  // NOTE: window.innerWidth-based sizing replaced with sx breakpoints below
  // so sizing reliably reacts to viewport changes (resize/rotate) instead of
  // only whatever width happened to be true at the last render.
  const dropdownButtonStyles = {
    padding: "13px 7px",
    fontSize: { xs: "10px", sm: "12px" },
    cursor: "pointer",
    color: Theme.palette.primary.main,
    fontWeight: 500,
    width: { xs: "70px", sm: "80px" },
    height: { xs: "32px", sm: "36px" },
    display: "flex",
    alignItems: "center",
    position: "relative",
    justifyContent: "space-between",
    [LANDSCAPE_SHORT]: {
      padding: "6px 6px",
      fontSize: "9px",
      width: "60px",
      height: "24px",
    },
  };

  const dropdownContentStyles = {
    position: "absolute",
    top: "100%",
    left: "0%",
    backgroundColor: "#fff",
    width: "100%",
    boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
    zIndex: 1000,
    borderRadius: "4px",
    border: "1px solid #ddd",
    marginTop: "4px",
    overflow: "hidden",
  };

  const dropdownItemStyles = {
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: { xs: "10px", sm: "12px" },
    backgroundColor: "#fff",
    "&:hover": { backgroundColor: "#f5f5f5" },
    [LANDSCAPE_SHORT]: {
      padding: "6px 8px",
      fontSize: "9px",
    },
  };

  const selectedItemStyles = {
    ...dropdownItemStyles,
    backgroundColor: "#defae5ff",
    fontWeight: 600,
    color: Theme.palette.primary.main,
  };

  return (
    <>
      {/* Language + Mode column — unchanged from original, stays on the left */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          [LANDSCAPE_SHORT]: {
            transform: "scale(0.85)",
            transformOrigin: "left center",
          },
        }}
      >
        {/* Language Dropdown */}
        <Box sx={{ position: "relative" }}>
          <Box
            onClick={() => {
              setLanguageOpen(!languageOpen);
              setModeOpen(false);
            }}
            sx={dropdownButtonStyles}
          >
            <span style={{ flex: 1, textAlign: "start" }}>
              {verification.aiData.spanish ? "SPANISH" : "ENGLISH"}
            </span>
            <span style={{ fontSize: "12px", color: Theme.palette.primary.main }}>▼</span>
          </Box>

          {languageOpen && (
            <Box sx={dropdownContentStyles}>
              <Box
                onClick={() => handleLanguageChange(false)}
                sx={!verification.aiData.spanish ? selectedItemStyles : dropdownItemStyles}
              >
                ENGLISH
              </Box>
              <Box
                onClick={() => handleLanguageChange(true)}
                sx={verification.aiData.spanish ? selectedItemStyles : dropdownItemStyles}
              >
                SPANISH
              </Box>
            </Box>
          )}
        </Box>

        {/* Mode Dropdown (AVATAR / READ) */}
        <Box sx={{ position: "relative" }}>
          <Box
            onClick={() => {
              setModeOpen(!modeOpen);
              setLanguageOpen(false);
            }}
            sx={{
              ...dropdownButtonStyles,
              opacity: verification?.aiData?.isAI ? 1 : 0.5,
              pointerEvents: verification?.aiData?.isAI ? "auto" : "none",
            }}
          >
            <span style={{ flex: 1, textAlign: "start" }}>
              {verification.aiData.spokeperson ? "AVATAR" : "READ"}
            </span>
            <span style={{ fontSize: "12px", color: Theme.palette.primary.main }}>▼</span>
          </Box>

          {modeOpen && verification?.aiData?.isAI && (
            <Box sx={dropdownContentStyles}>
              <Box
                onClick={() => {
                  verification.setAIData((prev) => ({ ...prev, spokeperson: true }));
                  setModeOpen(false);
                }}
                sx={verification.aiData.spokeperson ? selectedItemStyles : dropdownItemStyles}
              >
                AVATAR
              </Box>
              <Box
                onClick={() => {
                  verification.setAIData((prev) => ({ ...prev, spokeperson: false }));
                  setModeOpen(false);
                }}
                sx={!verification.aiData.spokeperson ? selectedItemStyles : dropdownItemStyles}
              >
                READ
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Dealership Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={verification?.dealershipLogo}
          alt="Dealership Logo"
          sx={{
            width: 150,
            height: 60,
            objectFit: "contain",
            [LANDSCAPE_SHORT]: {
              width: 90,
              height: 36,
            },
          }}
        />
      </Box>

      {/* Close Button — stays on the right, same as original, but now
          vertically CENTERED against the full toggle row height instead
          of pinned to the top. This is the actual fix: alignItems changed
          from "flex-start" to "center" so CLOSE lands level with the gap
          between the ENGLISH and AVATAR rows, not above them. */}
      {isClose ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "30%",
          }}
        >
          <AppButton
            buttonText={"close"}
            variant={"text"}
            sx={{
              color: Theme.palette.error.main,
              fontSize: { xs: "12px", sm: "16px" },
              [LANDSCAPE_SHORT]: { fontSize: "10px" },
            }}
            clickHandler={handleClose}
          />
        </Box>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", width: "30%" }} />
      )}
    </>
  );
};

export default ToggleComponent;